import { Link } from '@tanstack/react-router';
import { BellOff, Building2, FileText, Hash, ListChecks, MessageSquare, MoreHorizontal, Paperclip, Plus, SendHorizontal, User, Wrench, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  Spinner,
  Textarea,
  cn,
  useToast,
} from '@buildbee/ui';
import { errorMessage, trpc, type RouterOutputs } from '../../lib/trpc';
import { putToSignedUrl } from './lib';

/**
 * Team chat (web): channel list + thread + composer, shared by the workspace Messages page and the
 * project Chat tab. Realtime is polling: the open thread asks for changes (`since`) every few
 * seconds while the tab is visible; the channel list refreshes on a slower interval.
 */

export type ChatChannel = RouterOutputs['projects']['chat']['channels'][number];
export type ChatMessage = RouterOutputs['projects']['chat']['messages']['items'][number];

export const THREAD_POLL_MS = 4_000;
export const CHANNELS_POLL_MS = 20_000;

const KIND_ICON = { project: Building2, trade: Wrench, general: Hash, direct: User } as const;
const KIND_LABEL = { project: 'Project', trade: 'Trade', general: 'Channel', direct: 'Direct' } as const;

/** Channels the viewer belongs to (polled for unread badges). */
export function useChatChannels(projectId?: string) {
  return trpc.projects.chat.channels.useQuery(projectId ? { projectId } : {}, { refetchInterval: CHANNELS_POLL_MS, refetchIntervalInBackground: false });
}

/** Total unread messages, for a badge next to a Messages link. */
export function useChatUnread() {
  return trpc.projects.chat.unreadTotal.useQuery(undefined, { refetchInterval: 30_000, refetchIntervalInBackground: false, retry: false });
}

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

/**
 * Messages of one channel: the newest page first, older pages on demand, then polling for anything
 * created, edited or deleted since the newest change seen (merged by id, so overlaps are harmless).
 */
export function useChatThread(channelId: string | null) {
  const utils = trpc.useUtils();
  const [byId, setById] = useState<Map<string, ChatMessage>>(new Map());
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<unknown>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const since = useRef<Date>(new Date(0));
  const markRead = trpc.projects.chat.markRead.useMutation({ onSuccess: () => void utils.projects.chat.channels.invalidate() });
  const markReadRef = useRef(markRead.mutate);
  const byIdRef = useRef(byId);
  useEffect(() => {
    markReadRef.current = markRead.mutate;
    byIdRef.current = byId;
  });

  const merge = useCallback((items: ChatMessage[]) => {
    if (items.length === 0) return;
    for (const m of items) if (new Date(m.updatedAt) > since.current) since.current = new Date(m.updatedAt);
    setById((prev) => {
      const next = new Map(prev);
      for (const m of items) {
        const existing = next.get(m.id);
        // Keep the signed URLs already rendered for unchanged messages so images do not reload.
        next.set(m.id, existing && !m.deleted && new Date(existing.updatedAt).getTime() === new Date(m.updatedAt).getTime() ? existing : m);
      }
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    if (!channelId) return;
    setStatus('loading');
    setById(new Map());
    since.current = new Date(0);
    try {
      const page = await utils.client.projects.chat.messages.query({ channelId, limit: 40 });
      merge(page.items);
      setNextCursor(page.nextCursor);
      setStatus('ready');
      markReadRef.current({ channelId });
    } catch (err) {
      setError(err);
      setStatus('error');
    }
  }, [channelId, merge, utils]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!channelId || status !== 'ready') return;
    let busy = false;
    const timer = setInterval(async () => {
      if (busy || !pageVisible()) return;
      busy = true;
      try {
        const page = await utils.client.projects.chat.messages.query({ channelId, since: since.current });
        const fromOthers = page.items.some((m) => !m.mine && !m.deleted && !byIdRef.current.has(m.id));
        merge(page.items);
        if (fromOthers) markReadRef.current({ channelId });
      } catch {
        // Offline or a transient error: the next tick retries.
      } finally {
        busy = false;
      }
    }, THREAD_POLL_MS);
    return () => clearInterval(timer);
  }, [channelId, status, merge, utils]);

  const loadOlder = useCallback(async () => {
    if (!channelId || !nextCursor) return;
    setLoadingOlder(true);
    try {
      const page = await utils.client.projects.chat.messages.query({ channelId, limit: 40, cursor: nextCursor });
      merge(page.items);
      setNextCursor(page.nextCursor);
    } finally {
      setLoadingOlder(false);
    }
  }, [channelId, nextCursor, merge, utils]);

  const messages = useMemo(
    () => [...byId.values()].filter((m) => !m.deleted).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() || a.id.localeCompare(b.id)),
    [byId],
  );

  const remove = useCallback((id: string) => {
    setById((prev) => {
      const next = new Map(prev);
      const m = next.get(id);
      if (m) next.set(id, { ...m, deleted: true });
      return next;
    });
  }, []);

  return { messages, status, error, retry: load, hasOlder: !!nextCursor, loadOlder, loadingOlder, add: merge, remove };
}

// ---------------------------------------------------------------------------
// Channel list
// ---------------------------------------------------------------------------

export function ChannelList({
  channels,
  activeId,
  onSelect,
  showProject = true,
}: {
  channels: ChatChannel[];
  activeId: string | null;
  onSelect: (id: string) => void;
  showProject?: boolean;
}) {
  return (
    <ul className="space-y-0.5" aria-label="Channels">
      {channels.map((c) => {
        const Icon = KIND_ICON[c.kind];
        const active = c.id === activeId;
        return (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onSelect(c.id)}
              aria-current={active ? 'true' : undefined}
              className={cn(
                'flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                active ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
              )}
            >
              <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className={cn('truncate text-sm', c.unreadCount > 0 && !c.muted ? 'font-semibold' : 'font-medium')}>{c.name}</span>
                  {c.muted ? <BellOff className="size-3 shrink-0 text-muted-foreground" aria-label="Muted" /> : null}
                </span>
                <span className="block truncate text-[12px] text-muted-foreground">
                  {showProject && c.kind === 'trade' && c.projectName ? `${c.projectName} · ` : ''}
                  {c.lastMessage ? `${c.lastMessage.mine ? 'You' : (c.lastMessage.authorName ?? 'Someone')}: ${c.lastMessage.preview}` : KIND_LABEL[c.kind]}
                </span>
              </span>
              {c.unreadCount > 0 ? (
                <Badge variant={c.muted ? 'default' : 'primary'} className="figures mt-0.5" aria-label={`${c.unreadCount} unread`}>
                  {c.unreadCount > 99 ? '99+' : c.unreadCount}
                </Badge>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Thread
// ---------------------------------------------------------------------------

function sameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}

function MessageItem({
  m,
  compact,
  projectId,
  onEdit,
  onDelete,
}: {
  m: ChatMessage;
  compact: boolean;
  projectId: string | null;
  onEdit: (m: ChatMessage) => void;
  onDelete: (m: ChatMessage) => void;
}) {
  const toast = useToast();
  const createPunch = trpc.projects.punch.create.useMutation();
  const name = m.author?.name ?? 'Former member';
  const images = m.attachments.filter((a) => a.isImage && a.url);
  const files = m.attachments.filter((a) => !a.isImage || !a.url);
  const toPunch = async () => {
    if (!projectId) return;
    const text = m.body.trim() || 'From team chat';
    try {
      const item = await createPunch.mutateAsync({ projectId, title: text.length > 120 ? `${text.slice(0, 117)}…` : text, description: m.body || null });
      toast.success('Punch item created', `${item.label} is on the punch list.`);
    } catch (err) {
      toast.error('Could not create the punch item', errorMessage(err));
    }
  };
  return (
    <li className={cn('group relative flex gap-3 rounded-lg px-2 hover:bg-muted/50', compact ? 'py-0.5' : 'pt-2 pb-0.5')}>
      <div className="w-8 shrink-0">{compact ? null : <Avatar name={name} size="sm" />}</div>
      <div className="min-w-0 flex-1">
        {compact ? null : (
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold">{m.mine ? 'You' : name}</span>
            <time className="text-[12px] text-muted-foreground" dateTime={new Date(m.createdAt).toISOString()}>
              {new Date(m.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </time>
          </div>
        )}
        {m.body ? (
          <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed">
            {m.body}
            {m.editedAt ? <span className="ml-1 text-[12px] text-muted-foreground">(edited)</span> : null}
          </p>
        ) : null}
        {images.length ? (
          <div className="mt-1.5 flex flex-wrap gap-2">
            {images.map((a) => (
              <a key={a.key} href={a.url!} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-lg shadow-card">
                <img src={a.url!} alt={a.name} loading="lazy" className="max-h-64 max-w-[min(20rem,100%)] object-cover" />
              </a>
            ))}
          </div>
        ) : null}
        {files.length ? (
          <ul className="mt-1.5 flex flex-wrap gap-2">
            {files.map((a) => (
              <li key={a.key}>
                <a
                  href={a.url ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1.5 text-sm hover:underline"
                >
                  <FileText className="size-4 text-muted-foreground" aria-hidden /> {a.name}
                </a>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {m.mine || projectId ? (
        <div className="absolute right-2 top-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon" className="size-7 [&_svg]:size-4" aria-label="Message actions">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {projectId ? (
                <DropdownMenuItem onSelect={() => void toPunch()}>
                  <ListChecks aria-hidden /> Create punch item
                </DropdownMenuItem>
              ) : null}
              {m.mine ? (
                <>
                  {projectId ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuItem onSelect={() => onEdit(m)}>Edit</DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive" onSelect={() => onDelete(m)}>
                    Delete
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
    </li>
  );
}

type PendingFile = { id: string; file: File; status: 'uploading' | 'done' | 'error'; ref?: { key: string; name: string; contentType: string; size: number } };

function Composer({ channelId, onSent, disabled }: { channelId: string; onSent: (m: ChatMessage) => void; disabled?: boolean }) {
  const toast = useToast();
  const utils = trpc.useUtils();
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<PendingFile[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const requestUpload = trpc.projects.chat.requestUpload.useMutation();
  const send = trpc.projects.chat.send.useMutation();

  useEffect(() => {
    setBody('');
    setFiles([]);
  }, [channelId]);

  const addFiles = async (list: File[]) => {
    const added = list.slice(0, 10).map((file) => ({ id: crypto.randomUUID(), file, status: 'uploading' as const }));
    setFiles((prev) => [...prev, ...added]);
    for (const p of added) {
      try {
        const contentType = p.file.type || 'application/octet-stream';
        const { key, uploadUrl } = await requestUpload.mutateAsync({ channelId, name: p.file.name, contentType, sizeBytes: Math.max(p.file.size, 1) });
        await putToSignedUrl(uploadUrl, p.file, contentType);
        setFiles((prev) => prev.map((f) => (f.id === p.id ? { ...f, status: 'done', ref: { key, name: p.file.name, contentType, size: p.file.size } } : f)));
      } catch (err) {
        setFiles((prev) => prev.map((f) => (f.id === p.id ? { ...f, status: 'error' } : f)));
        toast.error(`Could not upload ${p.file.name}`, errorMessage(err));
      }
    }
  };

  const uploading = files.some((f) => f.status === 'uploading');
  const ready = files.filter((f) => f.status === 'done' && f.ref).map((f) => f.ref!);
  const canSend = !disabled && !uploading && !send.isPending && (body.trim().length > 0 || ready.length > 0);

  const submit = async () => {
    if (!canSend) return;
    try {
      const m = await send.mutateAsync({ channelId, body: body.trim(), attachments: ready });
      setBody('');
      setFiles([]);
      onSent(m);
      void utils.projects.chat.channels.invalidate();
    } catch (err) {
      toast.error('Message not sent', errorMessage(err));
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <div className="border-t border-border p-3">
      {files.length ? (
        <ul className="mb-2 flex flex-wrap gap-2" aria-label="Attachments">
          {files.map((f) => (
            <li key={f.id} className={cn('inline-flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-[13px]', f.status === 'error' && 'text-destructive')}>
              {f.status === 'uploading' ? <Spinner className="size-3.5" label="Uploading" /> : <Paperclip className="size-3.5" aria-hidden />}
              <span className="max-w-[12rem] truncate">{f.file.name}</span>
              <button type="button" aria-label={`Remove ${f.file.name}`} onClick={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))}>
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex items-end gap-2">
        <input
          ref={fileInput}
          type="file"
          multiple
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.dwg"
          className="sr-only"
          aria-label="Attach files"
          onChange={(e) => {
            const list = Array.from(e.target.files ?? []);
            e.target.value = '';
            if (list.length) void addFiles(list);
          }}
        />
        <Button variant="secondary" size="icon" aria-label="Attach photos or files" disabled={disabled} onClick={() => fileInput.current?.click()}>
          <Paperclip />
        </Button>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={disabled ? 'This conversation is read-only' : 'Write a message'}
          aria-label="Message"
          disabled={disabled}
          className="max-h-40 min-h-10 flex-1 resize-none"
        />
        <Button aria-label="Send" onClick={() => void submit()} disabled={!canSend}>
          <SendHorizontal />
        </Button>
      </div>
    </div>
  );
}

export function Thread({ channel, projectLink = true }: { channel: ChatChannel; projectLink?: boolean }) {
  const toast = useToast();
  const utils = trpc.useUtils();
  const thread = useChatThread(channel.id);
  const [editing, setEditing] = useState<ChatMessage | null>(null);
  const [editBody, setEditBody] = useState('');
  const edit = trpc.projects.chat.edit.useMutation();
  const del = trpc.projects.chat.delete.useMutation();
  const mute = trpc.projects.chat.mute.useMutation({ onSuccess: () => void utils.projects.chat.channels.invalidate() });
  const leave = trpc.projects.chat.leave.useMutation({ onSuccess: () => void utils.projects.chat.channels.invalidate() });
  const bottom = useRef<HTMLDivElement>(null);
  const lastId = thread.messages[thread.messages.length - 1]?.id;

  useEffect(() => {
    bottom.current?.scrollIntoView?.({ block: 'end' });
  }, [lastId]);

  const saveEdit = async () => {
    if (!editing) return;
    try {
      thread.add([await edit.mutateAsync({ id: editing.id, body: editBody })]);
      setEditing(null);
    } catch (err) {
      toast.error('Could not edit the message', errorMessage(err));
    }
  };
  const remove = async (m: ChatMessage) => {
    try {
      await del.mutateAsync({ id: m.id });
      thread.remove(m.id);
    } catch (err) {
      toast.error('Could not delete the message', errorMessage(err));
    }
  };
  const Icon = KIND_ICON[channel.kind];

  return (
    <section className="flex min-h-0 flex-1 flex-col" aria-label={`Conversation: ${channel.name}`}>
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        <Icon className="size-4 text-muted-foreground" aria-hidden />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[15px] font-semibold">{channel.name}</h2>
          <p className="truncate text-[12px] text-muted-foreground">
            {KIND_LABEL[channel.kind]}
            {channel.companyName ? ` · ${channel.companyName}` : ''}
            {channel.projectId && channel.projectName ? (
              <>
                {' · '}
                {projectLink ? (
                  <Link to="/projects/$projectId" params={{ projectId: channel.projectId }} className="hover:underline">
                    {channel.projectName}
                  </Link>
                ) : (
                  channel.projectName
                )}
              </>
            ) : null}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="icon" aria-label="Channel options">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => mute.mutate({ channelId: channel.id, muted: !channel.muted })}>
              <BellOff aria-hidden /> {channel.muted ? 'Unmute notifications' : 'Mute notifications'}
            </DropdownMenuItem>
            {channel.kind === 'general' || channel.kind === 'trade' ? (
              <DropdownMenuItem className="text-destructive" onSelect={() => leave.mutate({ channelId: channel.id })}>
                Leave channel
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
        {thread.status === 'loading' ? (
          <LoadingState label="Loading messages" />
        ) : thread.status === 'error' ? (
          <ErrorState error={errorMessage(thread.error)} onRetry={() => void thread.retry()} />
        ) : thread.messages.length === 0 ? (
          <EmptyState icon={<MessageSquare />} title="No messages yet" description="Share site updates, photos and questions with everyone in this conversation." />
        ) : (
          <>
            {thread.hasOlder ? (
              <div className="mb-2 flex justify-center">
                <Button variant="outline" size="sm" onClick={() => void thread.loadOlder()} disabled={thread.loadingOlder}>
                  {thread.loadingOlder ? 'Loading…' : 'Load earlier messages'}
                </Button>
              </div>
            ) : null}
            <ol className="space-y-0">
              {thread.messages.map((m, i) => {
                const prev = thread.messages[i - 1];
                const created = new Date(m.createdAt);
                const newDay = !prev || !sameDay(new Date(prev.createdAt), created);
                const compact = !newDay && !!prev && prev.author?.id === m.author?.id && created.getTime() - new Date(prev.createdAt).getTime() < 5 * 60_000;
                return (
                  <li key={m.id} className="list-none">
                    {newDay ? (
                      <div className="my-3 flex items-center gap-3 text-[12px] text-muted-foreground" role="separator">
                        <span className="h-px flex-1 bg-border" />
                        {created.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                        <span className="h-px flex-1 bg-border" />
                      </div>
                    ) : null}
                    <ul>
                      <MessageItem
                        m={m}
                        compact={compact}
                        projectId={channel.projectId}
                        onEdit={(x) => {
                          setEditing(x);
                          setEditBody(x.body);
                        }}
                        onDelete={(x) => void remove(x)}
                      />
                    </ul>
                  </li>
                );
              })}
            </ol>
            <div ref={bottom} />
          </>
        )}
      </div>
      <Composer channelId={channel.id} onSent={(m) => thread.add([m])} />
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent
          title="Edit message"
          footer={
            <>
              <Button variant="outline" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button onClick={() => void saveEdit()} disabled={!editBody.trim() || edit.isPending}>
                Save
              </Button>
            </>
          }
        >
          <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={4} aria-label="Message" />
        </DialogContent>
      </Dialog>
    </section>
  );
}

// ---------------------------------------------------------------------------
// New conversation
// ---------------------------------------------------------------------------

export function NewConversationDialog({
  open,
  onOpenChange,
  projectId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** On a project: trade channels for this project and DMs with its team. */
  projectId?: string;
  onCreated: (channelId: string) => void;
}) {
  const toast = useToast();
  const utils = trpc.useUtils();
  const [mode, setMode] = useState<'direct' | 'general' | 'trade'>(projectId ? 'trade' : 'direct');
  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const [members, setMembers] = useState<string[]>([]);
  const [companyId, setCompanyId] = useState('');
  const people = trpc.projects.chat.people.useQuery(projectId ? { projectId } : {}, { enabled: open });
  const companies = trpc.projects.chat.tradeCompanies.useQuery({ projectId: projectId ?? '' }, { enabled: open && !!projectId });
  const openDirect = trpc.projects.chat.openDirect.useMutation();
  const createGeneral = trpc.projects.chat.createGeneral.useMutation();
  const createTrade = trpc.projects.chat.createTrade.useMutation();
  const pending = openDirect.isPending || createGeneral.isPending || createTrade.isPending;

  useEffect(() => {
    if (!open) return;
    setUserId('');
    setName('');
    setMembers([]);
    setCompanyId('');
  }, [open]);

  const submit = async () => {
    try {
      const ch =
        mode === 'direct'
          ? await openDirect.mutateAsync({ userId })
          : mode === 'general'
            ? await createGeneral.mutateAsync({ name, memberUserIds: members })
            : await createTrade.mutateAsync({ projectId: projectId!, companyId, name: name || null });
      await utils.projects.chat.channels.invalidate();
      onCreated(ch.id);
      onOpenChange(false);
    } catch (err) {
      toast.error('Could not start the conversation', errorMessage(err));
    }
  };

  const valid = mode === 'direct' ? !!userId : mode === 'general' ? name.trim().length > 0 : !!companyId;
  const modes: { value: typeof mode; label: string }[] = projectId
    ? [
        { value: 'trade', label: 'Trade channel' },
        { value: 'direct', label: 'Direct message' },
      ]
    : [
        { value: 'direct', label: 'Direct message' },
        { value: 'general', label: 'Channel' },
      ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="New conversation"
        footer={
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} disabled={!valid || pending}>
              {mode === 'direct' ? 'Open' : 'Create'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div role="radiogroup" aria-label="Conversation type" className="flex gap-2">
            {modes.map((m) => (
              <Button key={m.value} role="radio" aria-checked={mode === m.value} variant={mode === m.value ? 'primary' : 'outline'} size="sm" onClick={() => setMode(m.value)}>
                {m.label}
              </Button>
            ))}
          </div>
          {mode === 'direct' ? (
            <Field label="Person">
              <Select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder={people.isLoading ? 'Loading…' : 'Choose someone'}
                options={(people.data ?? []).map((p) => ({ value: p.userId, label: p.name }))}
              />
            </Field>
          ) : null}
          {mode === 'trade' ? (
            <>
              <Field label="Company" description="The company's people on this project join, with the project team.">
                <Select
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                  placeholder={companies.isLoading ? 'Loading…' : (companies.data?.length ?? 0) === 0 ? 'No companies on this team yet' : 'Choose a company'}
                  options={(companies.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
                />
              </Field>
              <Field label="Name (optional)">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Defaults to the company name" />
              </Field>
            </>
          ) : null}
          {mode === 'general' ? (
            <>
              <Field label="Channel name">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Estimating, Safety" />
              </Field>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Members</legend>
                <div className="max-h-56 space-y-1.5 overflow-y-auto">
                  {(people.data ?? []).map((p) => (
                    <label key={p.userId} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={members.includes(p.userId)}
                        onCheckedChange={(v) => setMembers((prev) => (v ? [...prev, p.userId] : prev.filter((x) => x !== p.userId)))}
                      />
                      {p.name}
                      <span className="text-muted-foreground">{p.role}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Two-pane workspace
// ---------------------------------------------------------------------------

export function ChatWorkspace({
  channels,
  loading,
  error,
  onRetry,
  activeId,
  onSelect,
  projectId,
  emptyDescription,
}: {
  channels: ChatChannel[] | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  activeId: string | null;
  onSelect: (id: string | null) => void;
  projectId?: string;
  emptyDescription: string;
}) {
  const [creating, setCreating] = useState(false);
  const active = channels?.find((c) => c.id === activeId) ?? null;
  const showList = !active;

  return (
    <div className="flex h-[calc(100dvh-14rem)] min-h-[28rem] overflow-hidden rounded-xl bg-card shadow-card">
      <aside className={cn('w-full shrink-0 flex-col border-r border-border md:flex md:w-72', showList ? 'flex' : 'hidden')} aria-label="Conversations">
        <div className="flex items-center justify-between px-3 pb-2 pt-3">
          <h2 className="text-[13px] font-medium uppercase tracking-wide text-muted-foreground">Conversations</h2>
          <Button variant="secondary" size="sm" onClick={() => setCreating(true)}>
            <Plus aria-hidden /> New
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          {loading ? (
            <LoadingState label="Loading conversations" />
          ) : error ? (
            <ErrorState error={errorMessage(error)} onRetry={onRetry} />
          ) : (channels?.length ?? 0) === 0 ? (
            <EmptyState icon={<MessageSquare />} title="No conversations" description={emptyDescription} />
          ) : (
            <ChannelList channels={channels!} activeId={activeId} onSelect={onSelect} showProject={!projectId} />
          )}
        </div>
      </aside>
      <div className={cn('min-w-0 flex-1 flex-col', showList ? 'hidden md:flex' : 'flex')}>
        {active ? (
          <>
            <div className="border-b border-border px-2 py-1 md:hidden">
              <Button variant="ghost" size="sm" onClick={() => onSelect(null)}>
                ← All conversations
              </Button>
            </div>
            <Thread key={active.id} channel={active} projectLink={!projectId} />
          </>
        ) : (
          <EmptyState className="m-auto" icon={<MessageSquare />} title="Pick a conversation" description="Choose a channel on the left, or start a new one." />
        )}
      </div>
      <NewConversationDialog open={creating} onOpenChange={setCreating} projectId={projectId} onCreated={(id) => onSelect(id)} />
    </div>
  );
}

/** "Messages" button with the unread count (projects list header). */
export function MessagesLink({ icon }: { icon: ReactNode }) {
  const unread = useChatUnread();
  const total = unread.data?.total ?? 0;
  return (
    <Button asChild variant="outline">
      <Link to="/projects/messages">
        {icon} Messages
        {total > 0 ? (
          <Badge variant="primary" className="figures" aria-label={`${total} unread`}>
            {total > 99 ? '99+' : total}
          </Badge>
        ) : null}
      </Link>
    </Button>
  );
}
